import { useEffect, useRef, useState } from 'react';

export type ScrollDirection = 'up' | 'down';

export interface UseScrollDirectionReturn {
  direction: ScrollDirection;
  scrollY: number;
  isPastOffset: boolean;
}

/**
 * Hook para detectar la dirección del scroll (hacia arriba o hacia abajo)
 * y si se superó cierto offset, optimizado para evitar re-renders continuos.
 * 
 * @param threshold Umbral mínimo de movimiento en píxeles antes de cambiar de dirección
 * @param offset Posición Y a partir de la cual isPastOffset será true
 */
export const useScrollDirection = (
  threshold = 10,
  offset = 80
): UseScrollDirectionReturn => {
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>('up');
  const [scrollY, setScrollY] = useState<number>(0);
  const [isPastOffset, setIsPastOffset] = useState<boolean>(false);
  const lastScrollY = useRef<number>(0);
  const ticking = useRef<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Obtención ultra-robusta de la posición de scroll compatible con todos los navegadores móviles y webviews
    const getScrollPosition = (): number => {
      return Math.max(
        0,
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      );
    };

    const initialScroll = getScrollPosition();
    lastScrollY.current = initialScroll;
    setScrollY(initialScroll);
    setIsPastOffset(initialScroll > offset);

    const updateScrollDirection = () => {
      try {
        const currentScrollY = getScrollPosition();
        const diff = currentScrollY - lastScrollY.current;

        // Actualizamos isPastOffset. React ignorará el update si el valor booleano no cambia.
        setIsPastOffset(currentScrollY > offset);

        // Solo actualizamos si el movimiento supera el umbral establecido
        if (Math.abs(diff) >= threshold) {
          const newDirection: ScrollDirection = diff > 0 ? 'down' : 'up';
          setScrollDirection(newDirection);
          lastScrollY.current = currentScrollY;
          // Nota: Hemos eliminado setScrollY(currentScrollY) aquí para evitar re-renderizar 
          // el componente consumidor (ej: Navbar) en cada píxel de scroll.
        }

      } catch (error) {
        console.error('Error in useScrollDirection hook:', error);
      } finally {
        ticking.current = false;
      }
    };

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScrollDirection);
        ticking.current = true;
      }
    };

    // Usamos passive listener para rendimiento excepcional en scroll móvil
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [threshold, offset]);

  return {
    direction: scrollDirection,
    scrollY,
    isPastOffset,
  };
};

